
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, FileText, Download, Trash2 } from "lucide-react";

interface CVUploadProps {
  cvUrl: string | undefined;
  setCvUrl: (url: string) => void;
}

export const CVUpload = ({ cvUrl, setCvUrl }: CVUploadProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  const uploadCV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select a file to upload.');
      }

      const file = event.target.files[0];
      
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Please upload a PDF or Word document.');
      }

      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB.');
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/cv.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('cvs')
        .upload(fileName, file, { upsert: true });

      if (uploadError) {
        throw uploadError;
      }

      // Use secure URL generation for private bucket
      const { generateSecureFileUrl } = await import('@/utils/secure-storage');
      const secureUrl = await generateSecureFileUrl('cvs', fileName);
      
      if (!secureUrl) {
        throw new Error('Failed to generate secure URL');
      }
      
      setCvUrl(secureUrl);

      toast({
        title: "CV uploaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error uploading CV",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const deleteCV = async () => {
    try {
      if (!cvUrl || !user?.id) return;

      const fileName = `${user.id}/cv.pdf`; // You might want to store the actual filename
      
      const { error } = await supabase.storage
        .from('cvs')
        .remove([fileName]);

      if (error) throw error;

      setCvUrl("");

      toast({
        title: "CV removed successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error removing CV",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const downloadCV = () => {
    if (cvUrl) {
      window.open(cvUrl, '_blank');
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        {cvUrl ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              <div>
                <p className="font-medium text-foreground">CV Uploaded</p>
                <p className="text-sm text-muted-foreground">Click to download or replace</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={downloadCV}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={deleteCV}
                className="gap-2 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">Upload your CV/Resume</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Supported formats: PDF, DOC, DOCX (Max 5MB)
            </p>
          </div>
        )}
        
        <div className="mt-4">
          <Input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={uploadCV}
            disabled={uploading}
            className="cursor-pointer"
          />
          {uploading && (
            <p className="text-sm text-muted-foreground mt-2">
              Uploading...
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
